// @flow
import * as React from 'react'
import styled from 'styled-components'
import { Col, Container, Row, config } from 'react-awesome-styled-grid'
import DriveToDownloadBlock from '@components/DriveToDownloadBlock'
import { GoodSansB3, GoodSansB5, QueensH3, colors } from '@components/shared'
import BackgroundGradient from '@components/BackgroundGradient'

const founderText = `We were friends before we were co-founders and wanted something like Dipsea to exist way before we actually decided to make it. As readers, we've always been drawn to the transformative power of fiction. Stories have the ability to teach us, transport us, and help us understand ourselves and the world around us in new and profound ways. But as women, we often felt that something was missing from the narratives we encountered – an authentic representation of female experiences, desires, and perspectives. We knew there was an opportunity to create something different. Stories that celebrated the complexities of love, intimacy, and desire in a way that resonated with us and our friends. We set off to build something new: audio-first, short-ish format, always safe – and female gaze, front and center. And so, Dipsea was born. So whether you're looking to escape into an epic romance, explore the intricacies of a new relationship, or simply revel in the power of spontaneous connection, we invite you dip your toe into our sea of stories.`

export default function OurFounders (): React.Node {
  return (
    <Section>
    <BackgroundGradient
      quadrant={2}
      overlayColor='blue'
      gradientColor={colors.midnightBlue}
      gradientPosition='top'
    />
    <ContainerDiv className='container our-founders'>
      <Container>
        <Row>
          <Col
            lg={5}
            xl={5}
            offset={{ lg: 1, xl: 1 }}
            align={{ xs: 'center', lg: 'flex-end' }}
          >
            <MobileHeader>Meet our co-founders.</MobileHeader>
            <FounderImg>
              <img src='/images/founder-headshot.png' />
            </FounderImg>
          </Col>
          <Col lg={5} xl={5} align={{ xs: 'center', lg: 'flex-start' }}>
            <TextContainer>
              <DekstopHeader>Meet our co-founders.</DekstopHeader>
              <FounderText>{founderText}</FounderText>
              <FounderSignatureSection>
                <Gina />
                <Faye />
              </FounderSignatureSection>
            </TextContainer>
          </Col>
        </Row>
        <Row align='center' justify='center'>
          <DownloadSpacer>
            <DriveToDownloadBlock />
          </DownloadSpacer>
        </Row>
      </Container>
    </ContainerDiv>
    </Section>
  )
}

const Section = styled.section`
  position: relative;
  overflow: hidden;
`
const ContainerDiv = styled.div`
  padding-top: 90px;
  position: relative;
  ${(props: any): string => config(props).media.md`
    padding-top: 140px;
  `}
  ${(props: any): string => config(props).media.lg`
    padding-top: 160px;
  `}
`
const FounderSignatureSection = styled.div`
  display: flex;
  flex-direction: row;
  > div {
    &#founder-faye {
      margin-left: 42px;
    }
  }
  ${(props: any): string => config(props).media.xl`
    > div {
      margin: 0;
      &#founder-faye {
        margin-left: 100px;
      }
    }
  `}
`
const FounderImg = styled.div`
  img {
    border-radius: 10px;
    width: 100%;
  }
  max-width: 400px;
  ${(props: any): string => config(props).media.lg`
    max-width: 600px;
  `}
`
const TextContainer = styled.div`
  max-width: 400px;
  ${(props: any): string => config(props).media.lg`
    max-width: 600px;
  `}
`
const FounderHeader = styled.h3`
  ${QueensH3};
  color: ${colors.sand};
  margin-bottom: 1.25rem;
`
const MobileHeader = styled(FounderHeader)`
  @media (min-width: 910px) {
    display: none;
  }
`
const DekstopHeader = styled(FounderHeader)`
  display: none;
  @media (min-width: 910px) {
    display: block;
  }
`
const FounderText = styled.p`
  ${GoodSansB3};
  color: ${colors.sand};
  margin: 1.5rem 0;
  ${(props: any): string => config(props).media.lg`
     margin: 0 0 1.5rem 0;
  `}
`
const DownloadSpacer = styled.div`
  margin: 98px 0 62px;
  ${(props: any): string => config(props).media.md`
    margin: 95px 0 65px;
  `}
  ${(props: any): string => config(props).media.lg`
    margin: 202px 0 104px;
  `}
  ${(props: any): string => config(props).media.xl`
    margin: 212px 0 104px;
  `}
`
const Gina = (): React.Node => {
  return (
    <div id='founder-gina'>
      <FounderSvg
        id='gina-signature'
        data-name='gina signature'
        xmlns='http://www.w3.org/2000/svg'
        viewBox='50 120 500 350'
      >
        <path d='M222.33,336.18c0-4.91,2.6-9.91,3.52-12.61,1.91-5.66,3.45-9.05,5.58-14.64q4-10.55,8.77-20.78c2.75-5.92,5.77-11.7,8.61-17.58,2.51-5.19,5.57-10.06,8.43-15,3.2-5.56,7-10.66,10.52-16a119.92,119.92,0,0,1,8.76-11.35c3.58-4.17,7.22-8.29,11-12.27a99.55,99.55,0,0,1,12.54-11.44c4.88-3.65,9.9-7.45,15.74-9.54,3.47-1.25,6.9-2.74,10.61-3.23,6.13-.81,12.74,3.17,15.36,8.92,2.1,4.63,3.6,9.29,3.09,14.47-.67,6.77-1,13.58-2.58,20.24-1.2,4.86-1.85,9.88-3.22,14.68s-2.92,9.55-4.35,14.29a135.53,135.53,0,0,1-8.23,20.85,89.06,89.06,0,0,1-21.28,28c-5.71,5-12.23,8.55-18.56,12.43-3.73,2.29-7.93,3.88-11.93,5.77a18,18,0,0,1-4.89,1c-3.57.52-6.92,1.69-10.36,2.53-4.54,1.11-9.24,1.22-13.75,2.39a91.25,91.25,0,0,1-11.44,2c-3.54.43-8.62,0-11.93-3.15' />
        <path d='M106.84,272.93c1.84.57,3.67,1.09,5.47,1.8a10.05,10.05,0,0,0,8.21-.89c3.2-1.67,6.66-2.8,9.69-4.83,7.46-5,15.13-9.67,22.11-15.44A159.8,159.8,0,0,0,172,234.28a114.45,114.45,0,0,0,15.78-24.18A120.09,120.09,0,0,0,196,189.6c1.61-5.82,3.48-11.72,3.7-17.83.17-4.72-.42-9.54-3.07-13.63-2.94-4.53-7.93-5.66-13.44-4.69a21.42,21.42,0,0,0-9.16,4.32c-3.05,2.28-6.47,4.22-9.42,6.55A62,62,0,0,0,153.52,176c-3.89,5.2-7.77,10.44-11.24,15.89a163.79,163.79,0,0,0-9.34,16.4c-4,8.15-7.94,16.31-11.71,24.55-2.61,5.68-4.83,11.59-7,17.49-2.51,6.77-4.23,13.84-7.54,20.34C97.48,284,96,327.85,96,327.85c-.32,8-.56,16,.81,23.89.93,5.37,1.59,10.92,5.07,15.54,3.36,4.45,6,3.77,8.38-.52,1.42-2.53,2.14-5.31,3.27-7.93,2.87-6.67,5.44-13.46,8.1-20.21,1-2.66,9-17.57,10.65-20.49' />
        <path d='M175.36,380.14c1.92.23,3.91-.58,5.78,1,.89.73,2.4-.24,3.26-1a27.75,27.75,0,0,0,4.42-4.71c3.89-5.58,8-11,11.94-16.55,5.33-7.46,11.15-14.59,15.36-22.83a6.86,6.86,0,0,1,3.1-2.9' />
        <path d='M219.22,321.8c1,2.37.35,4.76,0,7.09s-.61,4.28,1.85,5.54c1.43.74,1.23,2.66,2.43,3.75-2.08,2.36-2.46,5.44-2.85,8.34-.48,3.57-1.46,7-2.23,10.48a153.63,153.63,0,0,0-2.69,18.68,112.23,112.23,0,0,0-.63,17.59,66.22,66.22,0,0,0,2.8,14.21,19.75,19.75,0,0,0,4.49,8.64c2.44,2.52,4.7,2.37,7.27,0,2.78-2.52,3.41-6.18,5.08-9.28,1.18-2.2,1.92-4.7,2.92-7.05,2.06-4.78,4-9.62,6-14.44a109.51,109.51,0,0,1,9.08-16.82' />
        <path d='M445.54,401.89c-8.14-3.48-15.14-3.89-18.71-4.35-3.09-.4-6-1.54-9.08-1.87-6.66-.7-13.24-2.13-20-2.17-3,0-6.06-.5-9.08-.68a70.31,70.31,0,0,0-7.26.05c-7.19.34-14.36.44-21.55.17-4.45-.18-8.52-2.19-13-2.51a17.44,17.44,0,0,0-5.92.55c-4.6,1.33-9.26,2.63-13.86,3.82-5.36,1.39-11.18,1.65-16-2.41-.48-.4-1.06-.68-1.55-1.06-4-3-5.14-3-9.36-.55a126.1,126.1,0,0,0-10.89,7.38c-3.27,2.4-5.31,1.64-5.9-2.37a7,7,0,0,0-.68-2.54c-2.46-3-1-6.35-.93-9.54,0-2.37,1.22-4.51,1.54-6.83' />
        <path d='M281.78,383.81c-.81-1.8,1.71-21.77,2.05-23.65.47-2.62.89-5.25,1.31-7.88.34-2.16,1.34-12.43,2.85-14' />
        <path d='M151,342.3c2.6-5.81,5.9-7,8.28-8.46,3.63-2.2,7.63-3.48,11.45-5.18a8.22,8.22,0,0,1,4.15-.26c2.95.26,5.88.67,8.82,1,0,0,5.12.61,7.54,1a94.17,94.17,0,0,1,16.34,4.51c2.56.9,3.8.23,4.15-2.09' />
        <path d='M252.7,368.57c-5.47,13.78-4.06,18.62-3.91,21.29.14,2.45-.12,4.91.5,7.36.82,3.2,2.87,4,5.22,1.83,1-.92,1.85-2,2.87-2.87,1.79-1.52,3.45-3,6.26-2,1.68.62,3.48-.64,5.17-1.42s3.26-1.61,5,.4c1,1.18,2.82,1.18,4.08-.44a8.35,8.35,0,0,1,3.1-2.38' />
        <path d='M526.72,381.56c3.13,8-5.42,6.65-5.93,6.65-3.78,0-7.43,1.16-11.16,1.31-4,.17-7.76,1.45-11.66,2-6.25.94-12.47,2.21-18.69,3.36-2.6.47-5.22.86-7.81,1.15-4.15.46-8.09,1.82-12.18,2.53a39.21,39.21,0,0,0-10.89,3.11c-.94.47-6.95-.3-12.71,1.06-7.28,1.93-31.74,9.71-34.86,10.83a97,97,0,0,0-10.8,4.2c-6.4,3.22-11.94,7.55-13.79,15.15a8.54,8.54,0,0,0,3.87,9.49c4.51,2.69,9.43,3.31,14.52,4.09,6.55,1,12.77-.49,18.94-1.46,7.08-1.11,13.86-3.93,20.6-6.72a73.23,73.23,0,0,0,16.45-10,13.65,13.65,0,0,0,5.31-9.6c.1-1,.57-1.91.74-2.89.66-3.71.23-5.45-1.58-7.79a68.84,68.84,0,0,0-9.55-6.19' />
        <path d='M72.59,298.93c2.5.26,3.5-1.43,4.14-3.42,2.12-6.59,7.19-18.17,10-24.47,2.25-5.1,4.17-10.41,7.4-15,.4-.58.78-1.33,1.34-1.51L97.76,259c1,2.72,2.41,5.26,3.61,7.89a8,8,0,0,0,4.69,4.72' />
        <path d='M281.62,386.33c4.17-12,4.85-15.22,5.38-18.61.86-5.45,2.43-13,3.3-18.46,1.59-13.8-2.31-10.95-2.31-10.95' />
        <path d='M126.57,330.19a16.42,16.42,0,0,1,1.32,6.07,2.66,2.66,0,0,0,1.53,2.9c1.41.65,2.32-.18,3.1-1.08,4.48-5.17,10.23-8.89,15.11-13.6,1.4-1.35,5.93-2,5.93-2-.07,3.09-1.7,5.76-2.19,8.79-.6,3.71-.06,7.38-.41,11.05' />
        <path d='M148.78,295.78a33.63,33.63,0,0,0-7.85,3.11c7.33.45,6.61,1.31,12.63-2.9' />
      </FounderSvg>
      <FounderName>Gina Gutierrez</FounderName>
    </div>
  )
}

const Faye = (): React.Node => {
  return (
    <div id='founder-faye'>
      <FounderSvg
        id='faye-signature'
        data-name='faye signature'
        xmlns='http://www.w3.org/2000/svg'
        viewBox='50 120 500 350'
      >
        <path d='M131,141.75c-3,9.7-14.48,82.61-18.12,99.92-2.13,10.1-5.3,31.91-6.76,42.13-2.47,17.3-.89,32.89-.37,50.36' />
        <path d='M342.39,175.5c-6.39,12.5-34.75,104.73-38,115.41l-7.15,23.59c-2,6.47-5.32,15.58-5.32,22.34,1.27.47,3.86-3.3,4.64-4.41' />
        <path d='M72.14,226.09c12.13-4.91,12.45-5.52,24-11.64,15.47-8.18,26.93-16.94,43.25-21.06,3.69-.93,15.5-7.79,21.94-5.38,4,1.48.59,5.51,0,7.21a20.29,20.29,0,0,1-3.85,6.28' />
        <path d='M95.8,275.61c2.74,1.05,4.82-.86,7.71-1.41a27.92,27.92,0,0,0,5.32-1.77c16.47-6.75,29.64-13.38,46.11-20.13,1.95-.8,5.42-2,6.07-4-12,6.94-11.65,10.79-19,22.57S121.55,302.36,118,315.76c-.54,2-1,4.3.14,6a5,5,0,0,0,3.39,2.17,18,18,0,0,0,15-4.68c5-4.31,11.53-10.51,15.31-15.88,9.72-13.8,21.61-22.12,27.38-38A77.71,77.71,0,0,0,163,289.14a16,16,0,0,0-1.91,6.44c0,2.85,2.35,6,5.15,5.4,1.65-.34,2.8-1.81,3.8-3.18l6.9-9.48c-.61,3.26-.8,9.55,3.2,9.95S205,283,214.38,272.37c-.7,48.69-.7,77.22-21.6,125.52-2.31,5.34-13,28.25-15.77,33.39-4.67,8.76-20.29,29.91-35.45,26.63-7.15-1-27.82-24.39-29.29-52-.83-15.49-.48-47.19,20.53-69.61,4.67-5,26.08-21.56,36.22-28.49,16.7-11.41,34.3-17.67,40.93-21.31,16.2-8.9,47.47-14.93,51.67-16.83s4.38-4.62,5.8-9-.3-9.91-4.51-11.78c-3.46-1.53-7.65-.29-10.56,2.13s-4.79,5.83-6.52,9.19q-3,5.9-5.78,11.94c-3,6.72-5.8,13.69-6.48,21s-1.21,16.71,3.67,22.23c4.24,4.8,12.87,8.2,19.25,7.73s12.53-6.36,16-11.73' />
        <path d='M527.86,304.88c-1.53,2.35-3.05,2.4-4.58,4.75-.92-8.8,1.39-20.73-.66-23.4-9.43,1.44-18.89,11.14-28.27,15.16.3-9.44.51-18.62.82-28.06.05-1.68,2.28-7.83.61-8-.85-.09-3.84,2.3-5.74,6.76-2.58,6.07-28.39,48.79-32.57,48.76,3.69-11.89,9.2-50.16,3.89-55.52s-14.88-4-21.92-1.85c-18,5.47-38.25,13.33-68.18,39.49-7.62,6.66-28.87,29.52-39.58,66.63-3.55,12.3-3.4,32.15-2.05,44.87,1,9.66,2.94,16.88,7.45,25.48,3.49,6.63,8.16,14.1,15.62,14.76,3.54.31,7-1,10.14-2.7,34.64-19,53.12-66.35,56.73-78.92,13.2-45.91,8.78-78.94,5.53-86.05s-4.16-10.91-7.49-18.32c-1.43-3.19-4.19-4.45-7.67-4.16-2,.16-33,30.21-38.1,34.53a1.9,1.9,0,0,1-1.09.57c-1.09,0,4.63-25.38,7-33.31-10.76,6.12-19.54,15.25-29.3,23.77-1.19,1-7.48,6-8.81,5.12-.57-.37-3.61-8.2-3.89-8.81-1.78-3.87-3.51-8.43-3.15-14.15.09-1.47,2.83-8.48,6-16.06,1.79-4.27,4.79-10.33,6.57-14.66,6.35-15.36,8.84-17.32,14.7-31.34' />
      </FounderSvg>
      <FounderName>Faye Keegan</FounderName>
    </div>
  )
}

const FounderSvg = styled.svg`
  fill: none;
  stroke: ${colors.sand};
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 5px;
  height: 75px;
`
const FounderName = styled.p`
  ${GoodSansB5};
  font-size: 12px;
  text-transform: uppercase;
  color: ${colors.sand};
`
